import { type BrandColor } from "contexts/color-theme";
import { type StaticImageData } from "next/image";
import snowflakeHero from "../../../public/assets/solutions/snowflake-cdp/snowflake-hero.png";
import snowflakeAccordion1 from "../../../public/assets/solutions/snowflake-cdp/accordion-1.png";
import snowflakeAccordion2 from "../../../public/assets/solutions/snowflake-cdp/accordion-2.png";
import snowflakeAccordion3 from "../../../public/assets/solutions/snowflake-cdp/accordion-3.png";
import snowflakeAccordion4 from "../../../public/assets/solutions/snowflake-cdp/accordion-4.png";
import snowflakeAccordion5 from "../../../public/assets/solutions/snowflake-cdp/accordion-5.png";
import snowflakeWordImage from "../../../public/assets/solutions/snowflake-cdp/word-image.png";
import databricksHero from "../../../public/assets/solutions/databricks-cdp/databricks-hero.png";
import databricksAccordion1 from "../../../public/assets/solutions/databricks-cdp/accordion-1.png";
import databricksAccordion2 from "../../../public/assets/solutions/databricks-cdp/accordion-2.png";
import databricksAccordion3 from "../../../public/assets/solutions/databricks-cdp/accordion-3.png";
import databricksAccordion4 from "../../../public/assets/solutions/databricks-cdp/accordion-4.png";
import databricksAccordion5 from "../../../public/assets/solutions/databricks-cdp/accordion-5.png";
import databricksWordImage from "../../../public/assets/solutions/databricks-cdp/word-image.png";
import bigqueryHero from "../../../public/assets/solutions/google-bigquery-cdp/bigquery-hero.png";
import bigqueryAccordion1 from "../../../public/assets/solutions/google-bigquery-cdp/accordion-1.png";
import bigqueryAccordion2 from "../../../public/assets/solutions/google-bigquery-cdp/accordion-2.png";
import bigqueryAccordion3 from "../../../public/assets/solutions/google-bigquery-cdp/accordion-3.png";
import bigqueryAccordion4 from "../../../public/assets/solutions/google-bigquery-cdp/accordion-4.png";
import bigqueryAccordion5 from "../../../public/assets/solutions/google-bigquery-cdp/accordion-5.png";
import bigqueryWordImage from "../../../public/assets/solutions/google-bigquery-cdp/word-image.png";
import redshiftHero from "../../../public/assets/solutions/aws-redshift-cdp/redshift-hero.png";
import redshiftAccordion1 from "../../../public/assets/solutions/aws-redshift-cdp/accordion-1.png";
import redshiftAccordion2 from "../../../public/assets/solutions/aws-redshift-cdp/accordion-2.png";
import redshiftAccordion3 from "../../../public/assets/solutions/aws-redshift-cdp/accordion-3.png";
import redshiftAccordion4 from "../../../public/assets/solutions/aws-redshift-cdp/accordion-4.png";
import redshiftAccordion5 from "../../../public/assets/solutions/aws-redshift-cdp/accordion-5.png";
import redshiftWordImage from "../../../public/assets/solutions/aws-redshift-cdp/word-image.png";
import synapseHero from "../../../public/assets/solutions/azure-synapse-cdp/synapse-hero.png";
import synapseAccordion1 from "../../../public/assets/solutions/azure-synapse-cdp/accordion-1.png";
import synapseAccordion2 from "../../../public/assets/solutions/azure-synapse-cdp/accordion-2.png";
import synapseAccordion3 from "../../../public/assets/solutions/azure-synapse-cdp/accordion-3.png";
import synapseAccordion4 from "../../../public/assets/solutions/azure-synapse-cdp/accordion-4.png";
import synapseAccordion5 from "../../../public/assets/solutions/azure-synapse-cdp/accordion-5.png";
import synapseWordImage from "../../../public/assets/solutions/azure-synapse-cdp/word-image.png";

export type WarehouseType = "snowflake" | "databricks" | "bigquery" | "redshift" | "azure";

type WarehouseResource = {
  href: string;
  title: string;
  imageSrc: string;
  imageAlt: string;
};

type WarehouseQuote = {
  text: string;
  authorName: string;
  authorJob: string;
  logoUrl: string;

  /**
   * Used to override the company name for the quote block in cases where there is a
   * parent company name to use instead of the product name, e.g. Google Cloud vs BigQuery.
   * */
  companyName?: string;
};

export type WarehouseConfig = {
  name: string;
  color: BrandColor;
  logoUrl: string;
  words: readonly string[];
  heroImage: StaticImageData;
  accordionImages: readonly StaticImageData[];
  wordImage: StaticImageData;
  quote?: WarehouseQuote;
  resources?: WarehouseResource[];
};

export const WarehouseCdpVariants: Record<WarehouseType, WarehouseConfig> = {
  snowflake: {
    name: "Snowflake",
    color: "blue",
    logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/0675905e9dc52d47cd6464fc0a459671e59fd1d4-652x620.svg",
    words: [
      "Snowflake",
      "Dynamic Tables",
      "Import column descriptions",
      "Snowflake Cortex",
      "Notebooks",
      "Polaris Catalog",
      "Data Clean Room",
      "Intelligent sampling",
      "Row-level security",
      "Column-level security",
      "AWS PrivateLink",
      "External tables",
      "Apache Iceberg",
      "dbt integration",
      "Data Sharing",
      "Snowflake Streams",
      "Looker",
      "Tableau",
      "Sigma",
    ],
    heroImage: snowflakeHero,
    wordImage: snowflakeWordImage,
    accordionImages: [
      snowflakeAccordion1,
      snowflakeAccordion2,
      snowflakeAccordion3,
      snowflakeAccordion4,
      snowflakeAccordion5,
    ],
    quote: {
      text: "We’re witnessing a changing of the guard around AI and how marketers capitalize on this massive opportunity. Hightouch emerged as a Leader in the Customer Data Platform category by demonstrating many successful joint-customers using their CDP on the AI Data Cloud.",
      authorName: "Denise Persson",
      authorJob: "Chief Marketing Officer",
      logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/ba1646dbc9c784bcd8104bd62ede02790759ac71-728x173.svg",
    },
    resources: [
      {
        href: "https://www.snowflake.com/en/blog/new-era-lifecycle-marketing-ai-decisioning-snowflake/",
        title: "A New Era of Lifecycle Marketing with the AI Data Cloud and AI Decisioning",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/948c555ece083dbf3dc1730d6f260091d6635a46-1024x511.png",
        imageAlt: "An individual sitting at a laptop working.",
      },
      {
        href: "https://quickstarts.snowflake.com/guide/hightouch_cdp/",
        title: "Marketing Orchestration and Campaign Intelligence with Hightouch and Snowflake",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/6fe18a0b79b9ae796af3efe2350814077af9b5f1-2400x1256.png",
        imageAlt: "Hightouch + Snowflake.",
      },
      {
        href: "https://www.snowflake.com/en/blog/build-an-actionable-customer-360-in-the-data-cloud-with-hightouch-events/",
        title: "Build an Actionable Customer 360 in the Data Cloud with Hightouch Events",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/169cac6c48c6e42d6bc507e10835bc5d09be98e3-1200x600.png",
        imageAlt: "An abstract illustration of many data rows being connected to a single person.",
      },
      {
        href: "https://www.snowflake.com/en/blog/composable-cdps-healthcare-life-sciences-secure-data-insights/",
        title: "How Composable CDPs Empower Healthcare and Life Sciences with Secure, Actionable Data Insights",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/77d492cca1f25b00485a71ac969847e4f0f2b72b-1440x720.png",
        imageAlt: "An individual wearing a labcoat and stethoscope sitting at a laptop next to two pill bottles.",
      },
    ] as WarehouseResource[],
  },
  databricks: {
    name: "Databricks",
    color: "red",
    logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/ef36b9be793746e5911d52311f16ee79a70ae1e8-24x24.svg",
    words: [
      "Delta Live Tables",
      "DBRX",
      "Unity Catalog",
      "Data Clean Room",
      "AWS PrivateLink",
      "Notebooks",
      "External tables",
      "Apache Iceberg",
      "Import column descriptions",
      "dbt integration",
      "Intelligent sampling",
      "Row-level security",
      "Column-level security",
      "Delta Sharing",
      "Serverless compute",
      "Delta Lake",
      "Looker",
      "Tableau",
      "Sigma",
    ],
    heroImage: databricksHero,
    wordImage: databricksWordImage,
    accordionImages: [
      databricksAccordion1,
      databricksAccordion2,
      databricksAccordion3,
      databricksAccordion4,
      databricksAccordion5,
    ],
    quote: {
      text: "With the Databricks and Hightouch partnership, customers get the best of both platforms. They get the flexibility, scalability, and AI tooling from the Databricks Data Intelligence Platform, plus the ease of use of Hightouch’s audience activation, journey orchestration, and AI decisioning solutions. Together, we’re helping marketers increase marketing efficiency and effectiveness faster than ever.",
      authorName: "Rob Saker",
      authorJob: "Global VP of Commerce Industries GTM",
      logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/59e4fdd3b5ef082d0e991df58b6539be1dad4067-132x22.svg",
    },
    resources: [
      {
        href: "https://www.databricks.com/customers/fastned/hightouch",
        title: "Powering Europe’s most reliable car charging network",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/04996b0627fb694d63e8a25d64747c4e229c4133-3000x2003.jpg",
        imageAlt: "A person standing at a Fastned electric vehicle charging station.",
      },
      {
        href: "https://www.databricks.com/blog/building-complete-and-composable-cdp-lakehouse",
        title: "Building a complete and composable CDP on the Lakehouse",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/c67c65dc1a6214df84bf520872868b380d0dba9d-2400x1256.png",
        imageAlt: "Hightouch + Databricks.",
      },
      {
        href: "https://www.databricks.com/blog/2022/06/24/the-emergence-of-the-composable-customer-data-platform.html",
        title: "The Emergence of the Composable Customer Data Platform",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/b941ad13f24a1a41fb747996f6f43f0a0db92e83-2400x1256.jpg",
        imageAlt: "An abstract diagram of a Composable CDP architecture.",
      },
      {
        href: "https://www.databricks.com/blog/unlock-deeper-marketing-insights-hightouch-campaign-intelligence-and-databricks",
        title: "Unlock deeper marketing insights with Hightouch Campaign Intelligence and Databricks",
        imageSrc:
          "https://cdn.sanity.io/images/pwmfmi47/production/3dfe77ba696a31c169e2f5c55222036da4f1b55b-2400x1256.webp",
        imageAlt: "An illustration of Databricks and Hightouch logos connected to an abstract chart of data.",
      },
    ] as WarehouseResource[],
  },
  bigquery: {
    name: "Google BigQuery",
    color: "blue",
    logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/e2180974e1b69ce3622ea7c9813f51d758258c35-24x24.svg",
    words: [
      "Continuous Queries",
      "Vertex AI",
      "External tables",
      "Apache Iceberg",
      "Import column descriptions",
      "dbt integration",
      "Intelligent sampling",
      "Row-level security",
      "Column-level security",
      "Looker",
      "Tableau",
      "Sigma",
    ],
    heroImage: bigqueryHero,
    wordImage: bigqueryWordImage,
    accordionImages: [
      bigqueryAccordion1,
      bigqueryAccordion2,
      bigqueryAccordion3,
      bigqueryAccordion4,
      bigqueryAccordion5,
    ],
    quote: {
      text: "We’re witnessing a transformation in how marketers use AI to significantly improve business outcomes. Hightouch is a leading Google Cloud partner and has enabled many joint customers to leverage their CDP on BigQuery’s highly scalable and powerful data platform.",
      authorName: "Naveen Punjabi",
      authorJob: "Director, Analytics Partnerships\n",
      companyName: "Google Cloud",
      logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/786c39f9a25992bcf46aa7ec0068d6c0dfc0efce-181x28.svg",
    },
  },
  redshift: {
    name: "Redshift",
    color: "violet",
    logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/22095e21d881717862649069234fef9297222248-24x24.svg",
    words: [
      "SSH Tunneling",
      "External tables",
      "Apache Iceberg",
      "dbt integration",
      "Intelligent sampling",
      "Row-level security",
      "Column-level security",
      "Redshift Serverless",
      "Redshift Spectrum",
      "Looker",
      "Tableau",
      "Sigma",
    ],
    heroImage: redshiftHero,
    wordImage: redshiftWordImage,
    accordionImages: [
      redshiftAccordion1,
      redshiftAccordion2,
      redshiftAccordion3,
      redshiftAccordion4,
      redshiftAccordion5,
    ],
  },
  azure: {
    name: "Azure Synapse",
    color: "violet",
    logoUrl: "https://cdn.sanity.io/images/pwmfmi47/production/b7841261c441fd1376518851f87a02c4e82eeebf-43x41.svg",
    words: [
      "Azure Private Link",
      "Apache Iceberg",
      "dbt integration",
      "Intelligent sampling",
      "Looker",
      "Tableau",
      "Sigma",
    ],
    heroImage: synapseHero,
    wordImage: synapseWordImage,
    accordionImages: [synapseAccordion1, synapseAccordion2, synapseAccordion3, synapseAccordion4, synapseAccordion5],
  },
} as const;
